import { v4 as uuidv4 } from 'uuid';
import {
    add, buildIndex, layoutRow, makeLabel, measureRow, normalizeAngle, rotatePoint, roundPoint, rowOrigin, SEAT_RADIUS, seatAbs, seatKey,
    shapeCenter, sub,
} from './geometry';
import { fullZoneIndexes, nextItemLabel, rebaseZone } from './sections';
import type { Entrance, LabelScheme, Plan, PlanIndex, Point, Row, Seat, Selection, Shape, ShapeKind, Stage, TableShape, Zone } from './types';

const touchRow = (row: Row): Row =>
    row.row_number_position && row.row_number_position !== 'both' ? { ...row, row_number_position: 'both' } : row;

export const mapRows = (plan: Plan, fn: (row: Row, zone: Zone, zi: number, ri: number) => Row | null): Plan => {
    let changed = false;
    const zones = plan.zones.map((zone, zi) => {
        let zoneChanged = false;
        const rows: Row[] = [];
        zone.rows.forEach((row, ri) => {
            const next = fn(row, zone, zi, ri);
            if (next !== row) zoneChanged = true;
            if (next && (next === row || next.seats.length)) rows.push(next);
        });
        if (!zoneChanged) return zone;
        changed = true;
        return { ...zone, rows };
    });
    return changed ? { ...plan, zones } : plan;
};

const mapShapes = (plan: Plan, keys: Set<string>, fn: (s: Shape) => Shape | null): Plan => {
    if (!keys.size) return plan;
    const apply = <T extends Shape>(kind: ShapeKind, list: T[] | undefined): T[] | undefined => {
        if (!list) return list;
        let changed = false;
        const out: T[] = [];
        for (const s of list) {
            if (!keys.has(`${kind}:${s.id}`)) {
                out.push(s);
                continue;
            }
            changed = true;
            const next = fn(s);
            if (next) out.push(next as T);
        }
        return changed ? out : list;
    };
    const stages = apply('stage', plan.stages);
    const entrances = apply('entrance', plan.entrances);
    return stages === plan.stages && entrances === plan.entrances ? plan : { ...plan, stages, entrances };
};

const writeAbs = (zone: Zone, row: Row, seat: Seat, abs: Point): Seat => ({
    ...seat,
    position: roundPoint(sub(abs, rowOrigin(zone, row))),
});

export const transformSeats = (plan: Plan, keys: Set<string>, fn: (abs: Point) => Point): Plan =>
    mapRows(plan, (row, zone) => {
        if (!row.seats.some(s => keys.has(seatKey(s)))) return row;
        return touchRow({
            ...row,
            seats: row.seats.map(s => (keys.has(seatKey(s)) ? writeAbs(zone, row, s, fn(seatAbs(zone, row, s))) : s)),
        });
    });

export const fullRowIds = (plan: Plan, index: PlanIndex, seatKeys: Set<string>): Set<string> => {
    const counts = new Map<string, number>();
    seatKeys.forEach(k => {
        const r = index.seatRow.get(k);
        if (r) counts.set(r, (counts.get(r) ?? 0) + 1);
    });
    const out = new Set<string>();
    counts.forEach((n, rowId) => {
        const ref = index.rows.get(rowId)!;
        if (plan.zones[ref.zi].rows[ref.ri].seats.length === n) out.add(rowId);
    });
    return out;
};

export const moveSelection = (plan: Plan, index: PlanIndex, sel: Selection, d: Point): Plan => {
    if (!d.x && !d.y) return plan;
    const fullRows = fullRowIds(plan, index, sel.seats);
    const fullZones = fullZoneIndexes(plan, sel.seats);
    let next = mapRows(plan, (row, zone, zi) => {
        if (fullZones.has(zi)) return row;
        if (fullRows.has(row.uuid!)) return { ...row, position: roundPoint(add(row.position, d)) };
        if (!row.seats.some(s => sel.seats.has(seatKey(s)))) return row;
        return touchRow({
            ...row,
            seats: row.seats.map(s => (sel.seats.has(seatKey(s)) ? writeAbs(zone, row, s, add(seatAbs(zone, row, s), d)) : s)),
        });
    });
    if (fullZones.size) {
        next = { ...next, zones: next.zones.map((z, zi) => (fullZones.has(zi) ? { ...z, position: roundPoint(add(z.position ?? { x: 0, y: 0 }, d)) } : z)) };
    }
    next = mapShapes(next, sel.shapes, s => ({ ...s, position: roundPoint(add(s.position, d)) }));
    return next;
};

export const rotateSelection = (plan: Plan, sel: Selection, center: Point, deg: number): Plan => {
    if (!deg) return plan;
    const sections = new Set([...fullZoneIndexes(plan, sel.seats)].filter(zi => plan.zones[zi].section));
    let next = transformSeats(plan, sel.seats, p => rotatePoint(p, center, deg));
    if (sections.size) {
        next = {
            ...next,
            zones: next.zones.map((z, zi) => (sections.has(zi) && z.section
                ? { ...rebaseZone(z, rotatePoint(z.position ?? { x: 0, y: 0 }, center, deg)), section: { ...z.section, rotation: normalizeAngle(z.section.rotation + deg) } }
                : z)),
        };
    }
    next = mapShapes(next, sel.shapes, s => {
        if (s.type === 'polygon' && s.points && (s as Stage).variant !== 'table') {
            const abs = s.points.map(p => rotatePoint(add(p, s.position), center, deg));
            return polygonFromAbs(s, abs);
        }
        const c = rotatePoint(shapeCenter(s), center, deg);
        return {
            ...s,
            rotation: Math.round((((s.rotation || 0) + deg) % 360) * 100) / 100,
            position: roundPoint({ x: c.x - s.size.width / 2, y: c.y - s.size.height / 2 }),
        };
    });
    return next;
};

const polygonFromAbs = <T extends Shape>(s: T, abs: Point[]): T => {
    const minX = Math.min(...abs.map(p => p.x));
    const minY = Math.min(...abs.map(p => p.y));
    const maxX = Math.max(...abs.map(p => p.x));
    const maxY = Math.max(...abs.map(p => p.y));
    return {
        ...s,
        position: roundPoint({ x: minX, y: minY }),
        size: { width: Math.round(maxX - minX), height: Math.round(maxY - minY) },
        points: abs.map(p => roundPoint({ x: p.x - minX, y: p.y - minY })),
    };
};

export const deleteSelection = (plan: Plan, sel: Selection): Plan => {
    let next = sel.seats.size
        ? mapRows(plan, row => {
            if (!row.seats.some(s => sel.seats.has(seatKey(s)))) return row;
            return touchRow({ ...row, seats: row.seats.filter(s => !sel.seats.has(seatKey(s))) });
        })
        : plan;
    if (next !== plan && next.zones.some(z => z.section && !z.rows.length)) {
        const zones = next.zones.filter(z => !z.section || z.rows.length);
        next = { ...next, zones: zones.length ? zones : [{ name: 'Ground Floor', code: 'GF', position: { x: 0, y: 0 }, rows: [] }] };
    }
    next = mapShapes(next, sel.shapes, () => null);
    return next;
};

export const setSeatCategory = (plan: Plan, keys: Set<string>, category: string): Plan =>
    mapRows(plan, row => {
        if (!row.seats.some(s => keys.has(seatKey(s)) && (s.category ?? '') !== category)) return row;
        return { ...row, seats: row.seats.map(s => (keys.has(seatKey(s)) ? { ...s, category } : s)) };
    });

export const updateSeat = (plan: Plan, key: string, patch: Partial<Seat>): Plan =>
    mapRows(plan, row => {
        if (!row.seats.some(s => seatKey(s) === key)) return row;
        return { ...row, seats: row.seats.map(s => (seatKey(s) === key ? { ...s, ...patch } : s)) };
    });

export const updateRow = (plan: Plan, rowId: string, fn: (row: Row, zone: Zone) => Row): Plan =>
    mapRows(plan, (row, zone) => (row.uuid === rowId ? fn(row, zone) : row));

export const updateShape = (plan: Plan, key: string, patch: Partial<Shape>): Plan =>
    mapShapes(plan, new Set([key]), s => ({ ...s, ...patch } as Shape));

const newSeat = (number: string, category = ''): Seat => {
    const id = uuidv4();
    return { seat_number: number, seat_guid: id, uuid: id, position: { x: 0, y: 0 }, category };
};

export const buildRow = (zone: Zone, points: Point[], rowLabel: string, seatScheme: LabelScheme, category = ''): Row => {
    const origin = roundPoint(sub(points[0], zone.position ?? { x: 0, y: 0 }));
    const zonePos = zone.position ?? { x: 0, y: 0 };
    return {
        uuid: uuidv4(),
        row_number: rowLabel,
        row_number_position: 'both',
        position: origin,
        seats: points.map((p, i) => ({
            ...newSeat(makeLabel(i, seatScheme), category),
            position: roundPoint(sub(sub(p, zonePos), origin)),
        })),
    };
};

export const addRows = (plan: Plan, zi: number, rows: Row[]): Plan => ({
    ...plan,
    zones: plan.zones.map((z, i) => (i === zi ? { ...z, rows: [...z.rows, ...rows] } : z)),
});

export const addShape = (plan: Plan, kind: ShapeKind, shape: Shape): Plan =>
    kind === 'stage'
        ? { ...plan, stages: [...(plan.stages ?? []), shape as Stage] }
        : { ...plan, entrances: [...(plan.entrances ?? []), shape as Entrance] };

type RowEdit = { count?: number; spacing?: number; sweep?: number; angle?: number; keepFirst?: boolean };

export const reshapeRow = (row: Row, zone: Zone, edit: RowEdit, defaults: { spacing: number; seatScheme: LabelScheme }): Row => {
    const abs = row.seats.map(s => seatAbs(zone, row, s));
    const g = measureRow(abs, defaults.spacing);
    const next = {
        ...g,
        count: Math.max(1, Math.min(500, Math.round(edit.count ?? g.count))),
        spacing: Math.max(1, edit.spacing ?? g.spacing),
        sweep: edit.sweep ?? g.sweep,
        angle: edit.angle ?? g.angle,
    };
    let points = layoutRow(next);
    if (edit.keepFirst && abs.length) {
        const shift = sub(abs[0], points[0]);
        points = points.map(p => add(p, shift));
    }

    const seats: Seat[] = [];
    const startNumber = row.seats.length ? row.seats[row.seats.length - 1].seat_number : '';
    const numeric = /^\d+$/.test(startNumber);
    for (let i = 0; i < points.length; i++) {
        const existing = row.seats[i];
        const seat = existing ?? newSeat(
            numeric ? String(parseInt(startNumber, 10) + (i - row.seats.length + 1)) : makeLabel(i, defaults.seatScheme),
            row.seats[row.seats.length - 1]?.category ?? '',
        );
        seats.push(writeAbs(zone, row, seat, points[i]));
    }
    return touchRow({ ...row, seats });
};

export const renumberRows = (plan: Plan, rowIds: string[], start: number, scheme: LabelScheme): Plan => {
    const order = new Map(rowIds.map((id, i) => [id, i]));
    return mapRows(plan, row => {
        const i = order.get(row.uuid!);
        return i === undefined ? row : { ...row, row_number: makeLabel(i, scheme, start) };
    });
};

export const renumberSeats = (plan: Plan, rowIds: Set<string>, start: number, scheme: LabelScheme, reverse: boolean): Plan =>
    mapRows(plan, row => {
        if (!rowIds.has(row.uuid!)) return row;
        const n = row.seats.length;
        return { ...row, seats: row.seats.map((s, i) => ({ ...s, seat_number: makeLabel(reverse ? n - 1 - i : i, scheme, start) })) };
    });

export const reverseSeatOrder = (plan: Plan, rowIds: Set<string>): Plan =>
    mapRows(plan, row => {
        if (!rowIds.has(row.uuid!)) return row;
        const numbers = row.seats.map(s => s.seat_number);
        return touchRow({ ...row, seats: [...row.seats].reverse().map((s, i) => ({ ...s, seat_number: numbers[i] })) });
    });

export const pasteSelection = (
    target: Plan,
    source: Plan,
    sel: Selection,
    offset: Point,
    opts: { zoneIndex?: number; rowLabelFor: (zone: Zone, nth: number) => string },
): { plan: Plan; selection: Selection } => {
    const newSeats = new Set<string>();
    const newShapes = new Set<string>();
    const shapeIds = new Map<string, string>();
    const rowIds = new Map<string, string>();
    const added = new Map<number, Row[]>();
    let tables = Number(nextItemLabel(target)) - 1;

    source.zones.forEach((srcZone, szi) => {
        const zi = opts.zoneIndex ?? Math.min(szi, target.zones.length - 1);
        const zone = target.zones[zi];
        srcZone.rows.forEach(row => {
            const picked = row.seats.filter(s => sel.seats.has(seatKey(s)));
            if (!picked.length) return;
            const whole = picked.length === row.seats.length;
            const list = added.get(zi) ?? [];
            const id = uuidv4();
            rowIds.set(row.uuid!, id);
            const abs = add(rowOrigin(srcZone, row), offset);
            const isTable = whole && row.tableId && sel.shapes.has(`stage:${row.tableId}`);
            list.push({
                ...row,
                uuid: id,
                tableId: isTable ? row.tableId : undefined,
                row_number: isTable ? String(++tables) : whole ? opts.rowLabelFor({ ...zone, rows: [...zone.rows, ...list] }, 0) : row.row_number,
                row_number_position: 'both',
                position: roundPoint(sub(abs, zone.position ?? { x: 0, y: 0 })),
                seats: picked.map(s => {
                    const sid = uuidv4();
                    newSeats.add(sid);
                    return { ...s, uuid: sid, seat_guid: sid };
                }),
            });
            added.set(zi, list);
        });
    });

    const cloneShapes = <T extends Shape>(kind: ShapeKind, src: T[] = []): T[] =>
        src.filter(s => sel.shapes.has(`${kind}:${s.id}`)).map(s => {
            const id = uuidv4();
            shapeIds.set(s.id, id);
            newShapes.add(`${kind}:${id}`);
            return { ...s, id, position: roundPoint(add(s.position, offset)) };
        });
    const stages = cloneShapes('stage', source.stages).map(s => {
        if (!s.tableRow) return s;
        const row = rowIds.get(s.tableRow);
        return row ? { ...s, tableRow: row } : { ...s, tableRow: undefined, variant: undefined };
    });
    const entrances = cloneShapes('entrance', source.entrances);

    const zones = target.zones.map((z, zi) => {
        const rows = added.get(zi);
        if (!rows) return z;
        return {
            ...z,
            rows: [...z.rows, ...rows.map(r => {
                if (!r.tableId) return r;
                const sid = shapeIds.get(r.tableId);
                const shape = stages.find(st => st.id === sid);
                if (shape) shape.name = r.row_number;
                return { ...r, tableId: sid };
            })],
        };
    });

    return {
        plan: { ...target, zones, stages: [...(target.stages ?? []), ...stages], entrances: [...(target.entrances ?? []), ...entrances] },
        selection: { seats: newSeats, shapes: newShapes },
    };
};

export const linkTables = (plan: Plan, index: PlanIndex, sel: Selection): Selection => {
    const seats = new Set(sel.seats);
    const shapes = new Set(sel.shapes);
    fullRowIds(plan, index, sel.seats).forEach(rowId => {
        const ref = index.rows.get(rowId)!;
        const tableId = plan.zones[ref.zi].rows[ref.ri].tableId;
        if (tableId && index.shapes.has(`stage:${tableId}`)) shapes.add(`stage:${tableId}`);
    });
    sel.shapes.forEach(k => {
        const ref = index.shapes.get(k);
        if (!ref || ref.kind !== 'stage') return;
        const rowId = plan.stages![ref.index].tableRow;
        const rowRef = rowId ? index.rows.get(rowId) : undefined;
        if (rowRef) plan.zones[rowRef.zi].rows[rowRef.ri].seats.forEach(s => seats.add(seatKey(s)));
    });
    return seats.size === sel.seats.size && shapes.size === sel.shapes.size ? sel : { seats, shapes };
};

export const countTables = (plan: Plan) =>
    plan.zones.reduce((n, z) => n + z.rows.filter(r => r.tableId).length, 0);

const TABLE_GAP = 5;
type Size = { width: number; height: number };

export const tableShapeOf = (stage: Stage): TableShape =>
    stage.type === 'circle' ? 'round' : stage.type === 'polygon' ? 'triangle' : 'rect';

const rectSplit = (n: number, ends: number) => {
    const e = Math.max(0, Math.min(n, Math.round(ends)));
    const sides = n - e;
    const top = Math.ceil(sides / 2);
    const right = Math.ceil(e / 2);
    return { top, bottom: sides - top, right, left: e - right };
};

export const minTableSize = (shape: TableShape, n: number, spacing: number, ends = 0): Size => {
    if (shape === 'round') {
        const ring = (n * spacing) / (2 * Math.PI);
        const d = Math.max(30, 2 * (ring - SEAT_RADIUS - TABLE_GAP));
        return { width: d, height: d };
    }
    if (shape === 'triangle') {
        const side = Math.max(60, (Math.ceil(n / 3) + 1) * spacing);
        return { width: side, height: (side * Math.sqrt(3)) / 2 };
    }
    const split = rectSplit(n, ends);
    return { width: Math.max(40, Math.max(split.top, split.bottom, 1) * spacing), height: Math.max(20, Math.max(split.left, split.right) * spacing) };
};

export const fitTableSize = (shape: TableShape, n: number, spacing: number, size?: Size, ends = 0): Size => {
    const min = minTableSize(shape, n, spacing, ends);
    if (!size) return shape === 'rect' ? { ...min, height: Math.max(34, min.height) } : min;
    return { width: Math.max(min.width, size.width), height: Math.max(min.height, size.height) };
};

export const tableLayout = (shape: TableShape, center: Point, n: number, spacing: number, size?: Size, ends = 0) => {
    const sz = fitTableSize(shape, n, spacing, size, ends);
    const rect = { x: center.x - sz.width / 2, y: center.y - sz.height / 2, width: sz.width, height: sz.height };
    const off = SEAT_RADIUS + TABLE_GAP;

    if (shape === 'round') {
        const rx = sz.width / 2 + off;
        const ry = sz.height / 2 + off;
        const points = Array.from({ length: n }, (_, i) => {
            const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
            return { x: center.x + rx * Math.cos(a), y: center.y + ry * Math.sin(a) };
        });
        return { points, type: 'circle' as const, rect, polygon: undefined };
    }

    if (shape === 'triangle') {
        const rel = [{ x: sz.width / 2, y: 0 }, { x: sz.width, y: sz.height }, { x: 0, y: sz.height }];
        const verts = rel.map(p => add(p, rect));
        const points: Point[] = [];
        for (let side = 0; side < 3; side++) {
            const a = verts[side];
            const b = verts[(side + 1) % 3];
            const d = sub(b, a);
            const len = Math.hypot(d.x, d.y) || 1;
            const nrm = { x: d.y / len, y: -d.x / len };
            const k = Math.floor(n / 3) + (side < n % 3 ? 1 : 0);
            for (let j = 0; j < k; j++) {
                const f = (j + 0.5) / k;
                points.push({ x: a.x + d.x * f + nrm.x * off, y: a.y + d.y * f + nrm.y * off });
            }
        }
        return { points, type: 'polygon' as const, rect, polygon: rel };
    }

    const { top, bottom, left, right } = rectSplit(n, ends);
    const points = [
        ...Array.from({ length: top }, (_, i) => ({ x: rect.x + ((i + 0.5) * sz.width) / top, y: rect.y - off })),
        ...Array.from({ length: right }, (_, i) => ({ x: rect.x + sz.width + off, y: rect.y + ((i + 0.5) * sz.height) / right })),
        ...Array.from({ length: bottom }, (_, i) => ({ x: rect.x + sz.width - ((i + 0.5) * sz.width) / bottom, y: rect.y + sz.height + off })),
        ...Array.from({ length: left }, (_, i) => ({ x: rect.x - off, y: rect.y + sz.height - ((i + 0.5) * sz.height) / left })),
    ];
    return { points, type: 'square' as const, rect, polygon: undefined };
};

const tableStagePatch = (layout: ReturnType<typeof tableLayout>) => ({
    type: layout.type,
    position: roundPoint(layout.rect),
    size: { width: Math.round(layout.rect.width), height: Math.round(layout.rect.height) },
    points: layout.polygon?.map(roundPoint),
});

export const createTable = (
    zone: Zone,
    center: Point,
    opts: { count: number; shape: TableShape; spacing: number; label: string; seatScheme: LabelScheme; size?: Size; category?: string; endSeats?: number },
): { row: Row; stage: Stage } => {
    const ends = opts.shape === 'rect' ? Math.max(0, Math.min(opts.count, Math.round(opts.endSeats ?? 0))) : 0;
    const layout = tableLayout(opts.shape, center, opts.count, opts.spacing, opts.size, ends);
    const stageId = uuidv4();
    const row = { ...buildRow(zone, layout.points, opts.label, opts.seatScheme, opts.category ?? ''), tableId: stageId };
    const stage: Stage = {
        id: stageId,
        name: opts.label,
        variant: 'table',
        tableRow: row.uuid,
        rotation: 0,
        color: '#F3F4F6',
        strokeColor: '#9CA3AF',
        strokeWidth: 1.5,
        curveRadius: 6,
        endSeats: ends || undefined,
        ...tableStagePatch(layout),
    };
    return { row, stage };
};

export const reshapeTable = (
    plan: Plan,
    rowId: string,
    stageId: string,
    opts: { count?: number; shape?: TableShape; size?: Size; center?: Point; spacing: number; seatScheme: LabelScheme; endSeats?: number },
): Plan => {
    const stage = plan.stages?.find(s => s.id === stageId);
    const row = plan.zones.flatMap(z => z.rows).find(r => r.uuid === rowId);
    if (!stage || !row) return plan;
    const current = tableShapeOf(stage);
    const shape = opts.shape ?? current;
    const count = Math.max(1, Math.min(60, Math.round(opts.count ?? row.seats.length)));
    const ends = shape === 'rect' ? Math.max(0, Math.min(count, Math.round(opts.endSeats ?? stage.endSeats ?? 0))) : 0;
    const size = shape !== current || opts.endSeats !== undefined ? undefined : opts.size ?? stage.size;
    const center = opts.center ?? shapeCenter(stage);
    const rot = stage.rotation || 0;
    const layout = tableLayout(shape, center, count, opts.spacing, size, ends);
    const points = layout.points.map(p => rotatePoint(p, center, rot));
    const next = mapRows(plan, (r, zone) => {
        if (r.uuid !== rowId) return r;
        const seats = points.map((p, i) => {
            const base = r.seats[i] ?? newSeat(makeLabel(i, opts.seatScheme), r.seats[0]?.category ?? '');
            return writeAbs(zone, r, base, p);
        });
        return touchRow({ ...r, seats });
    });
    return updateShape(next, `stage:${stageId}`, { ...tableStagePatch(layout), endSeats: ends || undefined } as Partial<Stage>);
};

const rowsAbs = (plan: Plan, index: PlanIndex, rowIds: string[]) =>
    rowIds.map(id => {
        const ref = index.rows.get(id)!;
        const zone = plan.zones[ref.zi];
        const row = zone.rows[ref.ri];
        return { id, pts: row.seats.map(s => seatAbs(zone, row, s)) };
    });

const moveRows = (plan: Plan, deltas: Map<string, Point>): Plan =>
    mapRows(plan, row => {
        const d = deltas.get(row.uuid!);
        return d && (d.x || d.y) ? { ...row, position: roundPoint(add(row.position, d)) } : row;
    });

export const alignRows = (plan: Plan, index: PlanIndex, rowIds: string[], mode: 'left' | 'center' | 'right'): Plan => {
    const rows = rowsAbs(plan, index, rowIds).map(r => {
        const xs = r.pts.map(p => p.x);
        return { id: r.id, min: Math.min(...xs), max: Math.max(...xs) };
    });
    const target = mode === 'left'
        ? Math.min(...rows.map(r => r.min))
        : mode === 'right'
            ? Math.max(...rows.map(r => r.max))
            : rows.reduce((a, r) => a + (r.min + r.max) / 2, 0) / rows.length;
    const deltas = new Map(rows.map(r => {
        const cur = mode === 'left' ? r.min : mode === 'right' ? r.max : (r.min + r.max) / 2;
        return [r.id, { x: target - cur, y: 0 }];
    }));
    return moveRows(plan, deltas);
};

export const rowGapInfo = (plan: Plan, index: PlanIndex, rowIds: string[], fallbackSpacing: number) => {
    const rows = rowsAbs(plan, index, rowIds);
    if (rows.length < 2) return null;
    const g = measureRow(rows[0].pts, fallbackSpacing);
    const a = (g.angle * Math.PI) / 180;
    const nrm = { x: -Math.sin(a), y: Math.cos(a) };
    const centers = rows.map(r => r.pts.reduce((c, p) => ({ x: c.x + p.x / r.pts.length, y: c.y + p.y / r.pts.length }), { x: 0, y: 0 }));
    const proj = centers.map(c => (c.x - centers[0].x) * nrm.x + (c.y - centers[0].y) * nrm.y);
    const order = proj.map((v, i) => ({ v, i })).sort((x, y) => x.v - y.v);
    let gap = 0;
    for (let i = 1; i < order.length; i++) gap += order[i].v - order[i - 1].v;
    return { nrm, order: order.map(o => ({ id: rows[o.i].id, v: o.v })), gap: gap / (order.length - 1) };
};

export const spaceRows = (plan: Plan, index: PlanIndex, rowIds: string[], gap: number, fallbackSpacing: number): Plan => {
    const info = rowGapInfo(plan, index, rowIds, fallbackSpacing);
    if (!info) return plan;
    const base = info.order[0].v;
    const deltas = new Map(info.order.map((o, i) => {
        const shift = base + i * gap - o.v;
        return [o.id, { x: info.nrm.x * shift, y: info.nrm.y * shift }];
    }));
    return moveRows(plan, deltas);
};

export const setAccessible = (plan: Plan, keys: Set<string>, value: boolean): Plan =>
    mapRows(plan, row => {
        if (!row.seats.some(s => keys.has(seatKey(s)) && !!s.accessible !== value)) return row;
        return {
            ...row,
            seats: row.seats.map(s => {
                if (!keys.has(seatKey(s))) return s;
                const { accessible: _omit, ...rest } = s;
                return value ? { ...rest, accessible: true } : rest;
            }),
        };
    });

export const renameCategory = (plan: Plan, from: string, to: string): Plan => {
    const next = mapRows(plan, row =>
        row.seats.some(s => s.category === from)
            ? { ...row, seats: row.seats.map(s => (s.category === from ? { ...s, category: to } : s)) }
            : row,
    );
    return { ...next, categories: plan.categories.map(c => (c.name === from ? { ...c, name: to } : c)) };
};

export const removeCategory = (plan: Plan, name: string): Plan => {
    const next = mapRows(plan, row =>
        row.seats.some(s => s.category === name)
            ? { ...row, seats: row.seats.map(s => (s.category === name ? { ...s, category: '' } : s)) }
            : row,
    );
    return { ...next, categories: plan.categories.filter(c => c.name !== name) };
};

export const scaleShape = (s: Shape, rect: { x: number; y: number; width: number; height: number }): Shape => {
    if (s.type === 'polygon' && s.points) {
        const sx = s.size.width ? rect.width / s.size.width : 1;
        const sy = s.size.height ? rect.height / s.size.height : 1;
        return {
            ...s,
            position: roundPoint(rect),
            size: { width: Math.round(rect.width), height: Math.round(rect.height) },
            points: s.points.map(p => roundPoint({ x: p.x * sx, y: p.y * sy })),
        };
    }
    return { ...s, position: roundPoint(rect), size: { width: Math.round(rect.width), height: Math.round(rect.height) } };
};

export const polygonShape = (abs: Point[], name: string): Shape =>
    polygonFromAbs(
        {
            id: uuidv4(),
            name,
            type: 'polygon',
            position: { x: 0, y: 0 },
            size: { width: 0, height: 0 },
            color: '#E5E7EB',
            strokeColor: '#6B7280',
            strokeWidth: 2,
        } as Shape,
        abs,
    );

export const planStats = (plan: Plan) => {
    let seats = 0;
    let rows = 0;
    let uncategorized = 0;
    let accessible = 0;
    const byCategory = new Map<string, number>();
    const duplicateSeats: string[] = [];
    plan.zones.forEach(zone => {
        zone.rows.forEach(row => {
            rows++;
            const numbers = new Set<string>();
            let dup = false;
            row.seats.forEach(s => {
                seats++;
                if (s.accessible) accessible++;
                if (numbers.has(s.seat_number)) dup = true;
                numbers.add(s.seat_number);
                const c = s.category ?? '';
                if (!c || !plan.categories.some(cat => cat.name === c)) uncategorized++;
                else byCategory.set(c, (byCategory.get(c) ?? 0) + 1);
            });
            if (dup) duplicateSeats.push(`${zone.name} / Row ${row.row_number || '(blank)'}`);
        });
    });
    return { seats, rows, uncategorized, accessible, byCategory, duplicateSeats };
};

export const validSelection = (plan: Plan, sel: Selection): Selection => {
    const index = buildIndex(plan);
    return {
        seats: new Set([...sel.seats].filter(k => index.seats.has(k))),
        shapes: new Set([...sel.shapes].filter(k => index.shapes.has(k))),
    };
};

export const newShape = (kind: ShapeKind, rect: { x: number; y: number; width: number; height: number }, name: string): Shape =>
    kind === 'stage'
        ? {
            id: uuidv4(),
            name,
            type: 'square',
            position: roundPoint(rect),
            size: { width: Math.round(rect.width), height: Math.round(rect.height) },
            rotation: 0,
            color: '#FDE68A',
            strokeColor: '#D97706',
            strokeWidth: 2,
            curveRadius: 6,
        }
        : {
            id: uuidv4(),
            name,
            type: 'square',
            position: roundPoint(rect),
            size: { width: Math.round(rect.width), height: Math.round(rect.height) },
            rotation: 0,
            color: '#BBF7D0',
            strokeColor: '#16A34A',
            strokeWidth: 2,
        };


export const resetTiering = (plan: Plan): Plan => {
    const next = mapRows(plan, row =>
        row.seats.some(s => s.category)
            ? { ...row, seats: row.seats.map(s => (s.category ? { ...s, category: '' } : s)) }
            : row,
    );
    return { ...next, categories: [] };
};

export const setCategoryBasePrice = (plan: Plan, name: string, basePrice: number | undefined): Plan => ({
    ...plan,
    categories: plan.categories.map(c => {
        if (c.name !== name) return c;
        const { basePrice: _drop, ...rest } = c;
        return basePrice === undefined ? rest : { ...rest, basePrice };
    }),
});
